import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteFooter } from "@/components/site-footer";
import { detectSeoLocale } from "@/lib/seo-locale";
import { hreflangLinks, seoMeta, SITE_URL, DEFAULT_SEO_LOCALE } from "@/lib/seo";

export const Route = createFileRoute("/terms")({
  loader: async () => ({ lang: await detectSeoLocale() }),
  head: ({ loaderData }) => ({
    meta: seoMeta("terms", loaderData?.lang ?? DEFAULT_SEO_LOCALE, {
      path: "/terms",
      ogType: "article",
      robots: "index, follow",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/terms` }, ...hreflangLinks("/terms")],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="min-h-dvh bg-background">
      <main className="mx-auto max-w-2xl px-5 py-10">
        <p className="text-xs text-muted-foreground"><Link to="/" className="hover:underline">← Torna a Lumen</Link></p>
        <h1 className="mt-4 text-3xl font-bold tracking-tight">Termini e Condizioni</h1>
        <p className="mt-2 text-sm text-muted-foreground">Ultimo aggiornamento: 6 luglio 2026</p>

        <div className="prose prose-sm mt-6 max-w-none space-y-6 text-sm leading-relaxed text-foreground">
          <section>
            <h2 className="text-lg font-semibold">1. Chi siamo</h2>
            <p>
              Lumen è un servizio fornito da <strong>Lerna AI</strong> ("Lerna AI", "noi"). Utilizzando l'app
              o il sito web di Lumen, stai stipulando un contratto direttamente con Lerna AI.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">2. Accettazione dei termini</h2>
            <p>
              Creando un account o utilizzando Lumen accetti integralmente questi Termini. Se non sei d'accordo, non utilizzare il servizio.
              Devi avere almeno 14 anni; se sei minorenne, l'iscrizione deve essere approvata da un genitore o tutore.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">3. Descrizione del servizio</h2>
            <p>
              Lumen offre strumenti di studio basati su intelligenza artificiale: tutor conversazionale, generazione di quiz, flashcard, mappe mentali, podcast dagli appunti, planner e statistiche.
              Tutte le funzioni sono disponibili gratuitamente per gli utenti registrati: Lumen non prevede acquisti in-app né abbonamenti.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">4. Account e credenziali</h2>
            <p>
              Sei responsabile della riservatezza delle credenziali del tuo account e delle attività svolte tramite esso. Le informazioni fornite devono essere accurate e aggiornate.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">5. Uso accettabile</h2>
            <p>Ti impegni a non:</p>
            <ul className="list-disc pl-5">
              <li>utilizzare il servizio per attività illegali, fraudolente o spam;</li>
              <li>violare diritti di proprietà intellettuale di terzi;</li>
              <li>tentare di aggirare misure di sicurezza, sondare vulnerabilità, distribuire malware, effettuare scraping non autorizzato;</li>
              <li>usare Lumen per creare contenuti illeciti, offensivi, deepfake, disinformazione, materiale che sfrutta minori o incita all'odio;</li>
              <li>rivendere, sublicenziare o redistribuire il servizio senza il nostro consenso scritto.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold">6. Contenuti IA e responsabilità dell'utente</h2>
            <p>
              I contenuti generati dall'IA possono contenere errori o imprecisioni e non costituiscono consulenza professionale (medica, legale, finanziaria).
              Sei responsabile dei prompt che invii, di come utilizzi gli output, di verificarne l'accuratezza e di avere i diritti sui materiali che carichi.
              Ci riserviamo il diritto di moderare, filtrare o rimuovere contenuti e di sospendere account per violazioni ripetute.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">7. Proprietà intellettuale</h2>
            <p>
              Lerna AI mantiene la titolarità di Lumen, del software, della documentazione e del marchio. Ti concediamo una licenza limitata, non esclusiva e non trasferibile all'utilizzo del servizio per finalità di studio personale.
              Non puoi decompilare, effettuare reverse engineering o aggirare limiti tecnici.
            </p>
            <p>
              I contenuti che carichi restano tuoi; ci concedi una licenza limitata a ospitarli ed elaborarli al solo scopo di erogare il servizio.
              Se ritieni che un contenuto violi i tuoi diritti, contattaci per una richiesta di rimozione.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">8. Servizio gratuito</h2>
            <p>
              Lumen è offerto gratuitamente: non esistono piani a pagamento, acquisti in-app,
              rinnovi automatici né richieste di dati di pagamento. Eventuali funzioni avanzate
              (mappe illustrate, video di studio, allegati audio/foto, programmi, orario da foto)
              sono accessibili a tutti gli utenti autenticati.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">9. Sospensione e cessazione</h2>
            <p>
              Possiamo sospendere o terminare l'accesso in caso di violazione sostanziale dei presenti termini, rischi di sicurezza o frode, o violazioni gravi o ripetute delle policy.
              Alla cessazione potrai esportare i tuoi dati per un periodo ragionevole; successivamente saranno eliminati salvo obblighi di legge.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">10. Garanzie e limitazione di responsabilità</h2>
            <p>
              Il servizio è fornito "così com'è". Nei limiti consentiti dalla legge escludiamo qualsiasi garanzia implicita di commerciabilità o idoneità a uno scopo specifico e non garantiamo un servizio ininterrotto o privo di errori.
              Poiché il servizio è gratuito, la nostra responsabilità aggregata è limitata nella misura massima consentita dalla legge. Sono esclusi i danni indiretti, consequenziali o speciali (perdita di profitti, dati, avviamento).
              Nulla in questi termini limita responsabilità per dolo, frode, morte o lesioni personali dove non consentito dalla legge.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">11. Manleva</h2>
            <p>
              Ti impegni a manlevare Lerna AI da reclami di terzi derivanti dai tuoi contenuti, dall'uso illecito del servizio o dalla violazione dei presenti termini.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">12. Legge applicabile e foro</h2>
            <p>
              I presenti termini sono regolati dalla legge italiana. Il foro competente in via esclusiva è quello del luogo di residenza del consumatore, ove previsto da norme inderogabili.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">13. Modifiche</h2>
            <p>
              Possiamo aggiornare questi termini per motivi legali, tecnici o operativi. Ti avviseremo con ragionevole anticipo delle modifiche sostanziali.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">14. Contatti</h2>
            <p>Per domande sui termini scrivi a Lerna AI tramite l'assistenza in-app.</p>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}